import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import AuthSelectors from '../../../selectors/AuthSelectors';
import { SurveyTimerActions } from '../../../common/actions/SurveyTimerActions';
import { AuthActions } from '../../../actions';
import {
    OUTPUT_REPORT_DETAIL_PATH,
    PARTICIPANT_PATH,
} from '../../../constants/route.constants';
import { TranslationsContext } from '@tcc-online/use-translations';
import ReactQuestionnaire from '@tcc-online/react-questionnaire';
import QuestionnaireActions from '../../../participant/actions/QuestionnaireActions';
import { AppDispatch } from '../../../reducers/store';
import { useTheme } from '@tcc-online/react-theme-provider';
import InfoSelectors from '../../../selectors/InfoSelectors';
import useAllowedLanguages from '../../../hooks/useAllowedLanguages';
import LanguageSlice from '../../../common/slices/LanguageSlice';
import { NotificationActions } from '../../../common/actions/NotificationActions';
import IQuestionnaire from '../../../types/survey/IQuestionnaire';
import isRunningInIframe from '../../../common/helpers/isRunningInIframe';
import { Modal } from '@tcc-online/common-components';
import { mapParagraphs } from '../../../helpers/mapParagraphs';
import styles from './Questionnaire.module.scss';

export default function Questionnaire() {
    const urlParams = useParams<{ id?: string }>();
    const questionnaireId = urlParams?.id ? parseInt(urlParams.id, 10) : null;
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const locale = useSelector(LanguageSlice.state.getLocale);
    const authToken = useSelector(AuthSelectors.getAuthToken);
    const refreshToken = useSelector(AuthSelectors.getRefreshToken);

    const [questionnaire, setQuestionnaire] = useState<
        IQuestionnaire | undefined
    >(undefined);
    const timeoutRef = useRef<any>(null);
    const [acknowledgementModal, setAcknowledgementModal] = useState<
        string | null
    >(null);
    const pendingNavigationRef = useRef<(() => void) | null>(null);

    useAllowedLanguages(questionnaire?.languages);

    useEffect(() => {
        return () => {
            setQuestionnaire(undefined);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            stopLoading();
        };
    }, []);

    const { translations } = useContext(TranslationsContext);

    const customization = useSelector(InfoSelectors.getCustomization);
    const [theme] = useTheme();

    function handleModalClose() {
        setAcknowledgementModal(null);
        if (pendingNavigationRef.current) {
            pendingNavigationRef.current();
            pendingNavigationRef.current = null;
        }
    }

    return useMemo(
        () => (
            <>
                <Modal
                    isVisible={!!acknowledgementModal}
                    onClose={handleModalClose}
                    classNames={{ modal: styles.modal, close: styles.close }}
                >
                    {acknowledgementModal &&
                        mapParagraphs(acknowledgementModal)}
                </Modal>
                <ReactQuestionnaire
                    questionnaireId={questionnaireId as number}
                    accessToken={authToken}
                    refreshToken={refreshToken}
                    locale={locale as string}
                    onTimerStart={startTimer}
                    apiConfig={{
                        endpoint: import.meta.env
                            .REACT_APP_API_ENDPOINT as string,
                        params: import.meta.env
                            .REACT_APP_API_ENDPOINT_PARAMS as string,
                    }}
                    onLoadingStart={startLoading}
                    onLoadingEnd={stopLoading}
                    timeBeforeLoadingShows={4000}
                    onQuestionnaireClosed={(payload) => {
                        const {
                            displayResults,
                            reportId,
                            acknowledgement,
                            redirect,
                        } = payload;

                        const handleNavigation = () => {
                            dispatch(QuestionnaireActions.closeQuestionnaire());

                            if (isRunningInIframe()) {
                                window.parent.postMessage('finished', '*');
                            }

                            if (redirect) {
                                window.location.replace(redirect);
                                return;
                            }

                            if (displayResults && !!reportId) {
                                timeoutRef.current = setTimeout(
                                    () =>
                                        navigate(
                                            generatePath(
                                                OUTPUT_REPORT_DETAIL_PATH,
                                                {
                                                    id: reportId.toString(),
                                                },
                                            ),
                                            { relative: 'path' },
                                        ),
                                    500,
                                );
                            } else {
                                timeoutRef.current = setTimeout(
                                    () =>
                                        navigate(PARTICIPANT_PATH, {
                                            relative: 'path',
                                        }),
                                    500,
                                );
                            }
                        };

                        if (acknowledgement) {
                            if (acknowledgement.length > 150) {
                                setAcknowledgementModal(acknowledgement);
                                pendingNavigationRef.current = handleNavigation;
                            } else {
                                dispatch(
                                    NotificationActions.info(
                                        acknowledgement,
                                        undefined,
                                        {},
                                        false,
                                        6000,
                                    ),
                                );
                                handleNavigation();
                            }
                        } else {
                            handleNavigation();
                        }
                    }}
                    onSectionClosed={() => {
                        dispatch(SurveyTimerActions.setCurrentTime(0));
                        dispatch(SurveyTimerActions.setCurrentId(null));
                    }}
                    translations={translations}
                    showOutputReport={false}
                    onLogout={() => dispatch(AuthActions.logout())}
                    onQuestionnaireLoad={onQuestionnaireLoad}
                    onSelfAssignmentClose={handleSelfAssignmentClose}
                    theme={
                        customization?.questionnaireColor
                            ? {
                                  ...theme,
                                  primaryColor:
                                      customization.questionnaireColor,
                                  secondaryColor:
                                      customization.questionnaireColor,
                              }
                            : theme
                    }
                    overrideTheme={!!customization?.questionnaireColor}
                    onError={(status: number) => {
                        if (status === 404) {
                            navigate(PARTICIPANT_PATH, {
                                replace: true,
                                relative: 'path',
                            });
                        }
                    }}
                />
            </>
        ),
        [
            questionnaireId,
            authToken,
            refreshToken,
            translations,
            acknowledgementModal,
        ],
    );

    function onQuestionnaireLoad(questionnaire: any) {
        setQuestionnaire(questionnaire);
    }

    function startLoading() {
        if (!questionnaire) {
            dispatch(QuestionnaireActions.setLoading(true));
        }
    }

    function stopLoading() {
        dispatch(QuestionnaireActions.setLoading(false));
    }

    function startTimer(time: number, id: number) {
        dispatch(SurveyTimerActions.setCurrentId(id));
        dispatch(SurveyTimerActions.setCurrentTime(time));
    }

    function handleSelfAssignmentClose() {
        navigate(PARTICIPANT_PATH, { replace: true });
    }
}
